/**
 * CloudFolderPicker — browse and select a cloud drive folder.
 * Shows breadcrumb navigation + folder list with select button.
 */
import { useState, useEffect } from "react";
import { Folder, ChevronRight, Check, Loader2, ArrowLeft } from "lucide-react";
import { api } from "../../lib/api.ts";
import { Button } from "../shared/Button.tsx";

interface CloudFolderPickerProps {
  projectId: string;
  onSelect: () => void; // called after folder is selected
}

interface BreadcrumbItem {
  id: string;
  name: string;
}

export function CloudFolderPicker({ projectId, onSelect }: CloudFolderPickerProps) {
  const [folders, setFolders] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(false);
  const [currentId, setCurrentId] = useState("root");
  const [currentName, setCurrentName] = useState("My Drive");
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([{ id: "root", name: "My Drive" }]);
  const [error, setError] = useState<string | null>(null);

  const loadFolders = (parentId: string) => {
    setLoading(true);
    setError(null);
    api.getCloudFolders(projectId, parentId)
      .then(({ folders: items, parentName }) => {
        setFolders(items);
        setCurrentName(parentName);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load folders"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadFolders("root"); }, [projectId]);

  const navigateToFolder = (folderId: string, folderName: string) => {
    setCurrentId(folderId);
    setBreadcrumbs(prev => [...prev, { id: folderId, name: folderName }]);
    loadFolders(folderId);
  };

  const navigateToBreadcrumb = (index: number) => {
    const target = breadcrumbs[index]!;
    setCurrentId(target.id);
    setBreadcrumbs(prev => prev.slice(0, index + 1));
    loadFolders(target.id);
  };

  const handleSelect = async () => {
    setSelecting(true);
    try {
      await api.selectCloudFolder(projectId, currentId, breadcrumbs.map(b => b.name).join(" / "));
      onSelect();
    } catch (err: any) {
      setError(err instanceof Error ? err.message : "Failed to select folder");
      setSelecting(false);
    }
  };

  return (
    <div className="bg-surface border border-border rounded-2xl p-4">
      <h3 className="text-[15px] font-medium mb-3">Select your project folder</h3>
      <p className="text-[13px] text-text-tertiary mb-4">
        Choose the folder where your project documents are stored. enereg will scan this folder and its subfolders for relevant documents.
      </p>

      {/* Breadcrumb navigation */}
      <div className="flex items-center gap-1 text-[13px] mb-3 overflow-x-auto pb-1">
        {breadcrumbs.map((crumb, i) => (
          <div key={crumb.id} className="flex items-center gap-1 flex-shrink-0">
            {i > 0 && <ChevronRight size={12} className="text-text-tertiary" />}
            <button
              onClick={() => navigateToBreadcrumb(i)}
              className={`px-1.5 py-0.5 rounded transition-colors ${
                i === breadcrumbs.length - 1
                  ? "text-text-primary font-medium"
                  : "text-text-secondary hover:text-text-primary hover:bg-surface-secondary"
              }`}
            >
              {crumb.name}
            </button>
          </div>
        ))}
      </div>

      {/* Folder list */}
      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 size={16} className="text-text-tertiary animate-spin" />
          <span className="text-[13px] text-text-tertiary ml-2">Loading folders...</span>
        </div>
      ) : error ? (
        <div className="text-[13px] text-danger py-4 text-center">{error}</div>
      ) : (
        <div className="border border-border rounded-xl overflow-hidden mb-4 max-h-64 overflow-y-auto">
          {breadcrumbs.length > 1 && (
            <button
              onClick={() => navigateToBreadcrumb(breadcrumbs.length - 2)}
              className="flex items-center gap-2 w-full px-3 py-2 text-[14px] text-text-secondary hover:bg-surface-secondary transition-colors border-b border-border"
            >
              <ArrowLeft size={14} /> Back
            </button>
          )}
          {folders.length === 0 ? (
            <div className="px-3 py-4 text-[13px] text-text-tertiary text-center">
              No subfolders here. You can select this folder.
            </div>
          ) : (
            folders.map((folder) => (
              <button
                key={folder.id}
                onClick={() => navigateToFolder(folder.id, folder.name)}
                className="flex items-center gap-2 w-full px-3 py-2 text-left text-[14px] hover:bg-surface-secondary transition-colors border-b border-border last:border-b-0"
              >
                <Folder size={16} className="text-warning-text flex-shrink-0" />
                <span className="flex-1 truncate">{folder.name}</span>
                <ChevronRight size={14} className="text-text-tertiary flex-shrink-0" />
              </button>
            ))
          )}
        </div>
      )}

      {/* Select button */}
      <div className="flex items-center justify-between">
        <span className="text-[12px] text-text-tertiary">
          Current: <span className="font-medium">{breadcrumbs.map(b => b.name).join(" / ")}</span>
        </span>
        {/* Busy state: spinner via Button, the label carries the explanation */}
        <Button icon={Check} busy={selecting} onClick={handleSelect}>
          {selecting ? "Selecting..." : "Use this folder"}
        </Button>
      </div>
    </div>
  );
}
